import axios from "axios";
import { supabase } from "./supabaseClient";
import { getPaymentStatus } from "./boostApi";
import { isNativeAndroid, isNativeIOS, listingProductId, listingAppleProductId, runNativePurchase, runNativeApplePurchase } from "./nativeBilling";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

async function authHeader() {
  const { data } = await supabase.auth.getSession();
  const token = data?.session?.access_token;
  return token ? { Authorization: `Bearer ${token}` } : {};
}

// Monthly listing allowance + admin/paid state for the current user.
export async function getListingStatus() {
  const headers = await authHeader();
  const { data } = await axios.get(`${API}/listing/status`, { headers });
  return data; // { is_admin, unlimited, free_used, free_remaining, paid_available }
}

// Purchased listing-credit packs (LIVE Stripe price ids → credits). Non-expiring.
export const LISTING_PACKS = [
  { priceId: "price_1SxOTpCFqL4V7hfxuSqUZhOh", credits: 1, price: "£1.99", label: "1 listing" },
  { priceId: "price_1UDU0QIVUBblOsLnJCaeqtGq", credits: 10, price: "£14.99", label: "10 listings" },
  { priceId: "price_1UDU1hIVUBblOsLnKCmhN0Oi", credits: 20, price: "£24.99", label: "20 listings" },
];

// Start a listing-credit checkout. `returnPath` is where Stripe returns after
// success. `priceId` selects a pack (1/10/20); omitted => the single £1.99 listing.
export async function startListingCheckout(returnPath = "/add-event", priceId = null) {
  // Inside a native wrapper, buy the equivalent Apple/Google Play product instead
  // of opening Stripe. On success the entitlement (listing credits) is granted
  // server-side. iOS is checked first (its wrapper UA can also match Android).
  if (isNativeIOS()) {
    await runNativeApplePurchase(listingAppleProductId(priceId));
    return { native: true, ok: true };
  }
  if (isNativeAndroid()) {
    await runNativePurchase(listingProductId(priceId));
    return { native: true, ok: true };
  }
  const headers = await authHeader();
  const { data } = await axios.post(
    `${API}/listing/checkout`,
    { origin_url: window.location.origin, return_path: returnPath, price_id: priceId },
    { headers }
  );
  return data; // { checkout_url, session_id }
}

// Pre-save check for a qualifying edit (date/time/location change): enforces the
// LIVE lock and that a listing entitlement exists. Consumes nothing.
export async function authorizeUpdate(eventId) {
  const headers = await authHeader();
  const { data } = await axios.post(
    `${API}/listing/authorize-update`,
    { event_id: eventId },
    { headers }
  );
  return data; // { allowed, listing_type }
}

// Post-save: record exactly ONE additional listing usage for a qualifying edit.
export async function consumeUpdate(eventId) {
  const headers = await authHeader();
  const { data } = await axios.post(
    `${API}/listing/consume-update`,
    { event_id: eventId },
    { headers }
  );
  return data; // { listing_type }
}

// Record exactly one listing usage after a successful publication.
export async function consumeListing(eventId) {
  const headers = await authHeader();
  const { data } = await axios.post(
    `${API}/listing/consume`,
    { event_id: eventId },
    { headers }
  );
  return data; // { listing_type }
}

// Claim the Founding Organiser offer. Call ONLY when the organiser profile just
// transitioned to complete (server enforces the atomic max-20 / once-per-user cap).
export async function claimFoundingOffer() {
  const headers = await authHeader();
  const { data } = await axios.post(`${API}/founding/claim`, {}, { headers });
  return data; // { claimed, remaining }
}

// Admin-only: activate the 7-day Boost on an owned event for free.
export async function adminActivateBoost(eventId) {
  const headers = await authHeader();
  const { data } = await axios.post(
    `${API}/boost/admin-activate`,
    { event_id: eventId },
    { headers }
  );
  return data;
}

// After returning from Stripe checkout, verify the payment server-side so the
// paid entitlement becomes available.
export async function confirmListingPayment(sessionId) {
  try {
    return await getPaymentStatus(sessionId);
  } catch {
    return null;
  }
}
